<template>
  <div>
    <div class="columns">
      <div class="column is-one-fifth">
        <p class="has-text-weight-bold is-size-6 mb-sm">Retailer Notes</p>
        <p class="has-text-grey is-size-7 mb-md">
          Internal reference of per-retailer onboarding nuances (e.g. invoicing
          rules) for the support &amp; onboarding team. Not shown to suppliers.
        </p>
        <!-- Retailer selector -->
        <b-field label="Retailer">
          <b-autocomplete
            v-model="retailerSearch"
            :data="retailerOptions"
            field="name"
            icon="storefront-outline"
            placeholder="Search retailers..."
            clearable
            :loading="retailerLoading"
            @typing="searchRetailers"
            @select="selectRetailer">
            <template v-slot="props">
              <span>{{ props.option.name }}</span>
              <span class="has-text-grey is-size-7"> &middot; {{ props.option.id }}</span>
            </template>
            <template #empty>
              {{ retailerLoading ? 'Searching...' : 'No matching retailers.' }}
            </template>
          </b-autocomplete>
        </b-field>
        <b-button
          type="is-primary rounded-lg"
          expanded
          :disabled="retailer === null"
          @click="add">
          Add Note
        </b-button>
      </div>
      <div class="column">
        <!-- No retailer selected yet -->
        <div v-if="retailer === null" class="has-background-white has-text-grey-light has-text-centered py-3xl rounded-xl">
          <p><b-icon icon="storefront-outline" size="is-large"></b-icon></p>
          <p>Select a retailer to view &amp; manage its onboarding notes.</p>
        </div>
        <template v-else>
          <!-- Toolbar -->
          <div class="has-background-white rounded-lg pa-md mb-xxs">
            <div class="columns is-vcentered is-mobile">
              <div class="column is-narrow">
                <div class="buttons has-addons is-marginless">
                  <b-button
                    v-for="pill in statusPills"
                    :key="pill.value"
                    size="is-small"
                    rounded
                    :type="statusFilter === pill.value ? 'is-primary' : 'is-light'"
                    @click="setStatus(pill.value)">
                    {{ pill.label }}
                  </b-button>
                </div>
              </div>
              <div class="column has-text-right is-size-7 has-text-grey">
                <span v-if="!loading">{{ retailer.name }} &mdash; {{ total }} {{ total === 1 ? 'note' : 'notes' }}</span>
              </div>
            </div>
          </div>
          <!-- Results -->
          <div class="has-background-white rounded-lg">
            <b-table :data="notes" :mobile-cards="false" :loading="loading" hoverable class="rounded-lg is-size-7">
              <template>
                <!-- Topic -->
                <b-table-column label="Topic" cell-class="nowrap" width="160" v-slot="props">
                  <b-tag rounded>{{ props.row.topic }}</b-tag>
                </b-table-column>
                <!-- Title -->
                <b-table-column label="Note" v-slot="props">
                  <a href="#" class="has-text-weight-bold has-text-info" @click.prevent="edit(props.row)">{{ props.row.title }}</a>
                </b-table-column>
                <!-- Status -->
                <b-table-column label="Status" cell-class="nowrap" width="96" centered v-slot="props">
                  <b-tag rounded :type="props.row.status === 'live' ? 'is-success' : 'is-warning'">
                    {{ props.row.status === 'live' ? 'Live' : 'Draft' }}
                  </b-tag>
                </b-table-column>
                <!-- Updated -->
                <b-table-column label="Updated" cell-class="nowrap" width="160" v-slot="props">
                  <p>{{ $dates.utc(props.row.updated_at).local().format('MMM D, YYYY') }}</p>
                  <p v-if="props.row.updated_by" class="has-text-grey-light">{{ props.row.updated_by.name }}</p>
                </b-table-column>
                <!-- Actions -->
                <b-table-column label="" cell-class="nowrap" width="64" centered v-slot="props">
                  <a href="#" @click.prevent="edit(props.row)">Edit</a>
                </b-table-column>
              </template>
              <template #empty>
                <div class="py-xxl has-text-grey has-text-centered">
                  {{ (loading) ? 'Loading data...' : 'No notes for this retailer yet. Click "Add Note" to record the first nuance!' }}
                </div>
              </template>
            </b-table>
            <div class="px-md pb-md">
              <b-pagination
                :total="total"
                :current.sync="currentPage"
                :rounded="true"
                :per-page="perPage"
                @change="paginate">
              </b-pagination>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <b-modal
      :active.sync="showModal"
      has-modal-card
      :width="840"
      :can-cancel="['escape', 'x']"
      @close="note = null">
      <RetailerNoteModal
        v-if="showModal"
        :retailer="retailer"
        :note="note"
        @updated="load"
        @deleted="load" />
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import RetailerNoteModal from './RetailerNoteModal'

export default {
  components: {
    RetailerNoteModal
  },
  computed: {
    ...mapGetters(['api']),
    endpoint () {
      return this.api.baseUrl + '/v1/retailers/' + this.retailer.id + '/notes/'
    }
  },
  data () {
    return {
      // sessionStorage key remembering the selected retailer, so navigating
      // away and back reopens the same retailer's notes (same convention as
      // the Filterable mixin's session cache).
      key: 'settings.retailer-notes',
      // selector
      retailer: null,
      retailerSearch: '',
      retailerOptions: [],
      retailerLoading: false,
      retailerTimer: null,
      // notes list
      notes: [],
      loading: false,
      total: 0,
      currentPage: 1,
      perPage: 25,
      statusFilter: 'all',
      statusPills: [
        { value: 'all', label: 'All' },
        { value: 'live', label: 'Live' },
        { value: 'draft', label: 'Draft' }
      ],
      // modal
      showModal: false,
      note: null
    }
  },
  methods: {
    searchRetailers (text) {
      clearTimeout(this.retailerTimer)
      const query = String(text || '').trim()
      if (query.length < 2) {
        this.retailerOptions = []
        return
      }
      this.retailerLoading = true
      this.retailerTimer = setTimeout(() => {
        this.$http.get(this.api.baseUrl + '/v1/retailers/', { params: { search: query, limit: 10, order_by: 'name' } }).then(response => {
          this.retailerOptions = response.data.results
        }).finally(() => {
          this.retailerLoading = false
        })
      }, 250)
    },
    selectRetailer (retailer) {
      this.retailer = retailer || null
      this.notes = []
      this.total = 0
      this.currentPage = 1
      try {
        if (this.retailer === null) {
          sessionStorage.removeItem(this.key)
        } else {
          sessionStorage.setItem(this.key, JSON.stringify({ id: this.retailer.id, name: this.retailer.name }))
        }
      } catch (e) {}
      if (this.retailer !== null) this.load()
    },
    restoreRetailer () {
      try {
        const cached = JSON.parse(sessionStorage.getItem(this.key))
        if (cached && cached.id) {
          this.retailer = cached
          this.retailerSearch = cached.name
          this.load()
        }
      } catch (e) {}
    },
    setStatus (value) {
      this.statusFilter = value
      this.currentPage = 1
      this.load()
    },
    paginate (page) {
      this.currentPage = page
      this.load()
    },
    load () {
      if (this.retailer === null) return
      this.loading = true
      const params = {
        limit: this.perPage,
        page: this.currentPage,
        order_by: 'position'
      }
      if (this.statusFilter !== 'all') {
        params.status = this.statusFilter
      }
      this.$http.get(this.endpoint, { params }).then(response => {
        this.notes = response.data.results
        this.total = response.data.count
      }).finally(() => {
        this.loading = false
      })
    },
    add () {
      this.note = null
      this.showModal = true
    },
    edit (note) {
      this.note = note
      this.showModal = true
    }
  },
  mounted () {
    this.restoreRetailer()
  },
  beforeDestroy () {
    clearTimeout(this.retailerTimer)
  }
}
</script>
